import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useCallback } from 'react'
import { useForm } from 'react-hook-form'
import { useProductsClosedApi } from 'src/api/db/apiProductsClosed'
import { FormWrapper } from 'src/shared/components/FormComponents'
import { Column } from 'src/shared/components/Semantic/all'
import { formSchema, ProductTabProps, FormData } from './types'
import { useProductFormInitialization } from './hooks'
import { transformFormDataForUpdate } from './utils'
import { General } from './components/General'
import { Categories } from './components/Categories'
import { Pricing } from './components/Pricing'
import { PrintDetails } from './components/PrintDetails'
import { Recommendation } from './components/Recommendation'
import { Translations } from './components/Translations'
import { useLocation } from 'react-router-dom'
import { paths } from 'src/routes/paths'

export const ProductTab = ({
  productId,
  onSaveHandlerReady,
  onHasChangesChange,
}: ProductTabProps) => {
  const { pathname } = useLocation()
  const isMyAccount = pathname.startsWith(paths.myAccountInventory)

  const { productsClosed, updateProduct } = useProductsClosedApi({
    product_id: productId,
  })

  const product = productsClosed?.data?.[0]

  const methods = useForm<FormData>({
    resolver: zodResolver(formSchema),
  })

  useProductFormInitialization(product, methods)

  const handleUpdateProduct = useCallback(async () => {
    const values = methods.getValues()
    const updateData = transformFormDataForUpdate(values, Number(product?.id))
    await updateProduct.mutateAsync(updateData)
    // Reset form state after successful save
    methods.reset(values, { keepValues: true })
  }, [methods, product?.id, updateProduct])

  useEffect(() => {
    if (onSaveHandlerReady) {
      onSaveHandlerReady(handleUpdateProduct)
    }
  }, [onSaveHandlerReady, handleUpdateProduct])

  // Only report changes after product is loaded and form is initialized
  useEffect(() => {
    if (onHasChangesChange && product) {
      onHasChangesChange(methods.formState.isDirty)
    }
  }, [methods.formState.isDirty, onHasChangesChange, product])

  return (
    <FormWrapper methods={methods} onSubmit={() => handleUpdateProduct()}>
      <Column className="gap-4">
        <General methods={methods} />
        <Categories
          productId={productId}
          categoryProduct={product?.category_product ?? []}
        />
        <Translations methods={methods} />
        <Pricing methods={methods} isMyAccount={isMyAccount} />
        {!isMyAccount && <PrintDetails methods={methods} />}
        {!isMyAccount && <Recommendation methods={methods} />}
      </Column>
    </FormWrapper>
  )
}
